/**
 * <cart-recommendations> — "Others also bought" row inside the cart drawer.
 *
 * Two responsibilities, split on purpose:
 *
 *  1. The custom element handles its OWN add-button clicks and owns the Swiper
 *     instance for the markup it currently contains.
 *
 *  2. A module-level controller (CartRecommendationsController) listens for cart
 *     changes on `document` — this listener lives independently of the element,
 *     because the theme's cart.js rebuilds `.drawer__inner` on every cart change
 *     and that rebuild can momentarily render WITHOUT the recommendations element
 *     (the AJAX `?section_id=cart-drawer` render uses default section settings).
 *     The controller re-fetches the cart-drawer section through the DOM-derived
 *     section id (which DOES carry the merchant's collection setting) and
 *     re-inserts / replaces the recommendations block so it always reappears and
 *     its Swiper re-initializes.
 *
 * Server-side Liquid (snippets/cart-recommendations.liquid) stays the single
 * source of truth for which products are shown (single-variant + not in cart).
 */

/* ----------------------------------------------------------------------------
 * Custom element: add-button interaction + Swiper for its current markup.
 * ------------------------------------------------------------------------- */
class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._isAdding = false;
    this._swiper = null;
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    this.initSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  /**
   * (Re)initialize Swiper on the current markup. Destroys any prior instance
   * first, so it is safe to call after every innerHTML swap. Waits for the
   * deferred Swiper bundle if it hasn't parsed yet.
   */
  initSwiper() {
    const el = this.querySelector('[data-rec-swiper]');
    if (!el) {
      this.destroySwiper();
      return;
    }

    if (typeof window.Swiper === 'undefined') {
      this._swiperReadyTimer = window.setTimeout(() => this.initSwiper(), 50);
      return;
    }

    this.destroySwiper();
    this._swiper = new window.Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 10,
      watchOverflow: true,
      // Free drag, but don't let it rest past the last slide (kills the empty
      // trailing space after the final card).
      freeMode: {
        enabled: true,
        sticky: true,
      },
    });
  }

  destroySwiper() {
    if (this._swiperReadyTimer) {
      window.clearTimeout(this._swiperReadyTimer);
      this._swiperReadyTimer = null;
    }
    if (this._swiper) {
      try {
        this._swiper.destroy(true, true);
      } catch (e) {
        /* already gone */
      }
      this._swiper = null;
    }
  }

  _onClick(event) {
    const addButton = event.target.closest('[data-rec-add]');
    if (!addButton || addButton.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    const card = addButton.closest('[data-rec-card]');
    const variantId = addButton.dataset.variantId;
    if (!variantId) return;

    this._addToCart(variantId, card, addButton);
  }

  _addToCart(variantId, card, addButton) {
    this._isAdding = true;
    CartRecommendationsController.suspend();
    this._setCardLoading(card, addButton, true);

    const drawerSectionId = CartRecommendationsController.sectionId();
    const body = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: [drawerSectionId, 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    };

    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(body);

    fetch(`${window.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this._setCardLoading(card, addButton, false);
          return;
        }

        // Drop the clicked card immediately for instant feedback.
        if (card && card.parentElement) card.remove();

        this._patchCartItems(response, drawerSectionId);
        this._openDrawer();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: variantId,
            cartData: response,
          });
        }
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'cart-recommendations', cartData: response, variantId },
          })
        );
      })
      .catch((error) => {
        console.error('[cart-recommendations] add failed', error);
        this._setCardLoading(card, addButton, false);
      })
      .finally(() => {
        this._isAdding = false;
        // Re-render the recs from the freshest cart state (added product now
        // excluded), then resume the global listener.
        CartRecommendationsController.refresh().finally(() => CartRecommendationsController.resume());
      });
  }

  /** Repaint items list + footer + cart counts from the add response. */
  _patchCartItems(response, sectionKey) {
    const sectionHtml = response && response.sections && response.sections[sectionKey];
    if (sectionHtml) {
      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
        const source = html.querySelector(selector);
        const target = document.querySelector(selector);
        if (source && target) target.replaceWith(source);
      });
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-empty');
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
    }

    if (typeof response.item_count === 'number') {
      document.querySelectorAll('.cart-item__count').forEach((el) => {
        el.textContent = response.item_count;
      });
    }

    const bubbleHtml = response.sections && response.sections['cart-icon-bubble'];
    const bubbleTarget = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubbleTarget) {
      const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const source = parsed.querySelector('.shopify-section') || parsed.body.firstElementChild;
      if (source) bubbleTarget.innerHTML = source.innerHTML;
    }
  }

  _openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) {
      drawer.open();
    }
  }

  _setCardLoading(card, addButton, isLoading) {
    if (addButton) {
      addButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      addButton.classList.toggle('cart-recommendations__add--loading', isLoading);
    }
    if (card) {
      const spinner = card.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}

/* ----------------------------------------------------------------------------
 * Module-level controller: keeps the recommendations block alive across the
 * theme's cart-drawer re-renders. Bound to `document` once, independent of the
 * element's lifecycle.
 * ------------------------------------------------------------------------- */
const CartRecommendationsController = {
  _suspended: false,
  _pending: false,

  /** Don't react to cart:updated while an internal add is mid-flight. */
  suspend() {
    this._suspended = true;
  },
  resume() {
    this._suspended = false;
  },

  /** Cart-drawer section id, resolved from the live DOM wrapper. */
  sectionId() {
    const drawer = document.querySelector('cart-drawer');
    const wrapper = drawer && drawer.closest('.shopify-section');
    if (wrapper && wrapper.id) return wrapper.id.replace(/^shopify-section-/, '');
    return 'cart-drawer';
  },

  sectionUrl() {
    const base = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/$/, '');
    return `${base}?section_id=${encodeURIComponent(this.sectionId())}`;
  },

  onCartUpdated(event) {
    if (this._suspended) return;
    if (event && event.detail && event.detail.source === 'cart-recommendations') return;
    this.refresh();
  },

  /**
   * Fetch the cart-drawer section (through the id that carries the merchant's
   * collection setting) and make the live drawer's recommendations block match:
   *  - replace the existing <cart-recommendations> in place, or
   *  - insert a fresh one after <cart-drawer-items> if it was wiped, or
   *  - remove it if there are no recommendations.
   * Returns a promise so callers can sequence work after it.
   */
  refresh() {
    return fetch(this.sectionUrl())
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const fresh = html.querySelector('cart-recommendations');
        const hasCards = fresh && fresh.querySelector('[data-rec-card]');
        const existing = document.querySelector('cart-drawer cart-recommendations');

        if (!hasCards) {
          // Nothing to recommend now — remove any stale block.
          if (existing) existing.remove();
          return;
        }

        if (existing) {
          existing.innerHTML = fresh.innerHTML;
          existing.hidden = false;
          existing.initSwiper();
        } else {
          // Block was wiped by the drawer re-render — re-insert it.
          const anchor = document.querySelector('cart-drawer cart-drawer-items');
          if (!anchor || !anchor.parentNode) return;
          const node = fresh.cloneNode(true);
          anchor.parentNode.insertBefore(node, anchor.nextSibling);
          if (typeof node.initSwiper === 'function') node.initSwiper();
        }
      })
      .catch((error) => {
        console.error('[cart-recommendations] refresh failed', error);
      });
  },

  init() {
    document.addEventListener('cart:updated', (event) => this.onCartUpdated(event));
  },
};

CartRecommendationsController.init();
